import React, { useState, useContext } from "react";
import {
  Box,
  Input,
  Button,
  Heading,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Select,
  Checkbox,
  VStack,
  Text,
  FormErrorMessage,
  HStack,
  IconButton,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Textarea,
  useDisclosure,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrash,
  faQuestionCircle,
  faEye,
  faTable,
} from "@fortawesome/free-solid-svg-icons";
import { AuthContext } from "../context/AuthContext";

const QuestionnaireBuilder = () => {
  const navigate = useNavigate();
  const { sessionId } = useContext(AuthContext) || {};
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [formData, setFormData] = useState({
    title: "",
    fields: [],
  });
  const [errors, setErrors] = useState({});
  const [confirmChecked, setConfirmChecked] = useState(false);

  const addField = () => {
    setFormData({
      ...formData,
      fields: [
        ...formData.fields,
        {
          name: "",
          label: "",
          type: "text",
          options: [],
          maxSelections: 1,
        },
      ],
    });
  };

  const removeField = (index) => {
    setFormData({
      ...formData,
      fields: formData.fields.filter((_, i) => i !== index),
    });
    validateForm();
  };

  const handleFieldChange = (index, key, value) => {
    const updatedFields = formData.fields.map((field, i) =>
      i === index ? { ...field, [key]: value } : field
    );
    setFormData({ ...formData, fields: updatedFields });
    validateField(index, key, value);
  };

  const addOption = (fieldIndex) => {
    const updatedFields = formData.fields.map((field, i) =>
      i === fieldIndex ? { ...field, options: [...field.options, ""] } : field
    );
    setFormData({ ...formData, fields: updatedFields });
  };

  const updateOption = (fieldIndex, optionIndex, value) => {
    const updatedFields = formData.fields.map((field, i) =>
      i === fieldIndex
        ? {
            ...field,
            options: field.options.map((opt, j) =>
              j === optionIndex ? value : opt
            ),
          }
        : field
    );
    setFormData({ ...formData, fields: updatedFields });
    validateForm();
  };

  const removeOption = (fieldIndex, optionIndex) => {
    const updatedFields = formData.fields.map((field, i) =>
      i === fieldIndex
        ? {
            ...field,
            options: field.options.filter((_, j) => j !== optionIndex),
          }
        : field
    );
    setFormData({ ...formData, fields: updatedFields });
    validateForm();
  };

  const validateField = (index, key, value) => {
    let newErrors = { ...errors };
    const field = formData.fields[index];

    if (key === "name" && !value) {
      newErrors[`field_${index}_name`] = "กรุณากรอกชื่อฟิลด์";
    } else if (key === "name" && value.length > 50) {
      newErrors[`field_${index}_name`] = "ชื่อฟิลด์ต้องไม่เกิน 50 ตัวอักษร";
    } else if (key === "name") {
      delete newErrors[`field_${index}_name`];
    }

    if (key === "label" && !value) {
      newErrors[`field_${index}_label`] = "กรุณากรอกชื่อป้ายกำกับ";
    } else if (key === "label") {
      delete newErrors[`field_${index}_label`];
    }

    if (key === "type" && (value === "select" || value === "checkbox")) {
      if (field.options.length < 1 || field.options.some((opt) => !opt)) {
        newErrors[`field_${index}_options`] =
          "กรุณากรอกตัวเลือกอย่างน้อย 1 ตัวเลือก";
      } else {
        delete newErrors[`field_${index}_options`];
      }
    } else if (key === "type") {
      delete newErrors[`field_${index}_options`];
    }

    if (key === "maxSelections" && field.type === "checkbox") {
      if (value < 1 || value > field.options.length) {
        newErrors[
          `field_${index}_maxSelections`
        ] = `จำนวนที่เลือกได้ต้องอยู่ระหว่าง 1 ถึง ${field.options.length}`;
      } else {
        delete newErrors[`field_${index}_maxSelections`];
      }
    }

    setErrors(newErrors);
  };

  const validateForm = () => {
    let newErrors = {};
    if (!formData.title) {
      newErrors.title = "กรุณากรอกชื่อแบบสอบถาม";
    }

    formData.fields.forEach((field, index) => {
      if (!field.name) {
        newErrors[`field_${index}_name`] = "กรุณากรอกชื่อฟิลด์";
      } else if (field.name.length > 50) {
        newErrors[`field_${index}_name`] = "ชื่อฟิลด์ต้องไม่เกิน 50 ตัวอักษร";
      }
      if (!field.label) {
        newErrors[`field_${index}_label`] = "กรุณากรอกชื่อป้ายกำกับ";
      }
      if (["select", "checkbox"].includes(field.type)) {
        if (field.options.length < 1 || field.options.some((opt) => !opt)) {
          newErrors[`field_${index}_options`] =
            "กรุณากรอกตัวเลือกอย่างน้อย 1 ตัวเลือก";
        }
      }
      if (
        field.type === "checkbox" &&
        (field.maxSelections < 1 || field.maxSelections > field.options.length)
      ) {
        newErrors[
          `field_${index}_maxSelections`
        ] = `จำนวนที่เลือกได้ต้องอยู่ระหว่าง 1 ถึง ${field.options.length}`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = () => {
    return (
      formData.title.trim() !== "" &&
      formData.fields.every(
        (field) =>
          field.name.trim() !== "" &&
          field.label.trim() !== "" &&
          (["select", "checkbox"].includes(field.type)
            ? field.options.length > 0 &&
              field.options.every((opt) => opt.trim() !== "")
            : true) &&
          (field.type === "checkbox"
            ? field.maxSelections >= 1 &&
              field.maxSelections <= field.options.length
            : true)
      ) &&
      Object.keys(errors).length === 0 &&
      confirmChecked
    );
  };

  const handleSubmit = () => {
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลให้ครบถ้วนและถูกต้อง");
      return;
    }

    const submitData = {
      id: `q_${Date.now()}`,
      title: formData.title,
      fields: formData.fields.map((field) => ({
        name: field.name,
        label: field.label,
        type: field.type,
        options:
          field.type === "text" || field.type === "textarea"
            ? undefined
            : field.options,
        maxSelections:
          field.type === "checkbox" ? field.maxSelections : undefined,
      })),
    };

    // Save to local storage
    try {
      const existingQuestionnaires = JSON.parse(
        localStorage.getItem("questionnaires") || "[]"
      );
      existingQuestionnaires.push(submitData);
      localStorage.setItem(
        "questionnaires",
        JSON.stringify(existingQuestionnaires)
      );
      alert("สร้างแบบสอบถามสำเร็จ!");
      navigate("/questionnaire-list");
    } catch (error) {
      alert("เกิดข้อผิดพลาดในการบันทึกแบบสอบถาม: " + error.message);
    }
  };

  const renderPreviewField = (field, index) => {
    switch (field.type) {
      case "text":
        return (
          <FormControl key={index} mb={4}>
            <FormLabel>{field.label}</FormLabel>
            <Input isReadOnly />
          </FormControl>
        );
      case "textarea":
        return (
          <FormControl key={index} mb={4}>
            <FormLabel>{field.label}</FormLabel>
            <Textarea isReadOnly />
          </FormControl>
        );
      case "select":
        return (
          <FormControl key={index} mb={4}>
            <FormLabel>{field.label}</FormLabel>
            <Select isReadOnly>
              {field.options.map((option, optIndex) => (
                <option key={optIndex} value={option}>
                  {option}
                </option>
              ))}
            </Select>
          </FormControl>
        );
      case "checkbox":
        return (
          <FormControl key={index} mb={4}>
            <FormLabel>
              {field.label} (เลือกได้สูงสุด {field.maxSelections} ข้อ)
            </FormLabel>
            <VStack align="start">
              {field.options.map((option, optIndex) => (
                <Checkbox key={optIndex} isReadOnly>
                  {option}
                </Checkbox>
              ))}
            </VStack>
          </FormControl>
        );
      default:
        return null;
    }
  };

  return (
    <Box p={35} m={25} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <Box
        mb={5}
        borderRadius="full"
        w="100%"
        bgColor="iPink.500"
        color="white"
        p={2}
      >
        <HStack spacing={5} ml={5}>
          <FontAwesomeIcon icon={faTable} color="white" size="xl" />
          <Heading size="lg" fontWeight="normal">
            สร้างแบบสอบถาม
          </Heading>
        </HStack>
      </Box>

      <Box m={10}>
        {/* Box 1: ข้อมูลทั่วไป */}
        <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
          <Box
            mb={5}
            borderRadius="full"
            w="100%"
            bgColor="iTeal.500"
            color="white"
            p={2}
          >
            <HStack spacing={5} ml={5}>
              <FontAwesomeIcon
                icon={faQuestionCircle}
                color="white"
                size="xl"
              />
              <Heading size="lg" fontWeight="normal">
                ข้อมูลทั่วไป
              </Heading>
            </HStack>
          </Box>
          <Grid templateColumns="repeat(3, 1fr)" gap={6} p={5}>
            <GridItem colSpan={3}>
              <FormControl isRequired isInvalid={!!errors.title}>
                <FormLabel fontSize="xl" color="gray">
                  ชื่อแบบสอบถาม{" "}
                  <Text as="span" color="red">
                    {" "}
                    *
                  </Text>
                </FormLabel>
                <Input
                  value={formData.title}
                  onChange={(e) => {
                    setFormData({ ...formData, title: e.target.value });
                    setErrors({
                      ...errors,
                      title: e.target.value
                        ? undefined
                        : "กรุณากรอกชื่อแบบสอบถาม",
                    });
                  }}
                  colorScheme="iTeal"
                />
                <FormErrorMessage>{errors.title}</FormErrorMessage>
              </FormControl>
            </GridItem>
          </Grid>
        </Box>

        {/* Box 2: ฟิลด์แบบสอบถาม */}
        <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
          <Box
            mb={5}
            borderRadius="full"
            w="100%"
            bgColor="iBlue.500"
            color="white"
            p={2}
          >
            <HStack spacing={5} ml={5}>
              <FontAwesomeIcon
                icon={faQuestionCircle}
                color="white"
                size="xl"
              />
              <Heading size="lg" fontWeight="normal">
                ฟิลด์แบบสอบถาม
              </Heading>
            </HStack>
          </Box>
          <VStack align="stretch" spacing={6} p={5}>
            {formData.fields.map((field, fieldIndex) => (
              <Box key={fieldIndex} borderWidth={1} borderRadius="md" p={4}>
                <Grid templateColumns="repeat(3, 1fr)" gap={6}>
                  <GridItem>
                    <FormControl
                      isRequired
                      isInvalid={!!errors[`field_${fieldIndex}_name`]}
                    >
                      <FormLabel fontSize="xl" color="gray">
                        ชื่อฟิลด์{" "}
                        <Text as="span" color="red">
                          {" "}
                          *
                        </Text>
                      </FormLabel>
                      <Input
                        value={field.name}
                        onChange={(e) =>
                          handleFieldChange(fieldIndex, "name", e.target.value)
                        }
                        colorScheme="iBlue"
                      />
                      <FormErrorMessage>
                        {errors[`field_${fieldIndex}_name`]}
                      </FormErrorMessage>
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl
                      isRequired
                      isInvalid={!!errors[`field_${fieldIndex}_label`]}
                    >
                      <FormLabel fontSize="xl" color="gray">
                        ป้ายกำกับ{" "}
                        <Text as="span" color="red">
                          {" "}
                          *
                        </Text>
                      </FormLabel>
                      <Input
                        value={field.label}
                        onChange={(e) =>
                          handleFieldChange(fieldIndex, "label", e.target.value)
                        }
                        colorScheme="iBlue"
                      />
                      <FormErrorMessage>
                        {errors[`field_${fieldIndex}_label`]}
                      </FormErrorMessage>
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel fontSize="xl" color="gray">
                        ประเภท
                      </FormLabel>
                      <Select
                        value={field.type}
                        onChange={(e) =>
                          handleFieldChange(fieldIndex, "type", e.target.value)
                        }
                        colorScheme="iBlue"
                      >
                        <option value="text">Text</option>
                        <option value="textarea">Textarea</option>
                        <option value="select">Select</option>
                        <option value="checkbox">Checkbox</option>
                      </Select>
                    </FormControl>
                  </GridItem>
                </Grid>
                {["select", "checkbox"].includes(field.type) && (
                  <Box mt={4}>
                    <Text fontSize="xl" color="gray" mb={2}>
                      ตัวเลือก{" "}
                      <Text as="span" color="red">
                        {" "}
                        *
                      </Text>
                    </Text>
                    <VStack align="stretch" spacing={2}>
                      {field.options.map((option, optionIndex) => (
                        <HStack key={optionIndex}>
                          <Input
                            value={option}
                            onChange={(e) =>
                              updateOption(
                                fieldIndex,
                                optionIndex,
                                e.target.value
                              )
                            }
                            placeholder="กรอกตัวเลือก"
                            colorScheme="iBlue"
                          />
                          <IconButton
                            icon={<FontAwesomeIcon icon={faTrash} />}
                            colorScheme="red"
                            onClick={() =>
                              removeOption(fieldIndex, optionIndex)
                            }
                          />
                        </HStack>
                      ))}
                      <Button
                        leftIcon={<FontAwesomeIcon icon={faPlus} />}
                        colorScheme="iBlue"
                        onClick={() => addOption(fieldIndex)}
                      >
                        เพิ่มตัวเลือก
                      </Button>
                      <FormControl
                        isInvalid={!!errors[`field_${fieldIndex}_options`]}
                      >
                        <FormErrorMessage>
                          {errors[`field_${fieldIndex}_options`]}
                        </FormErrorMessage>
                      </FormControl>
                    </VStack>
                  </Box>
                )}
                {field.type === "checkbox" && (
                  <Box mt={4}>
                    <FormControl
                      isInvalid={!!errors[`field_${fieldIndex}_maxSelections`]}
                    >
                      <FormLabel fontSize="xl" color="gray">
                        จำนวนที่เลือกได้สูงสุด
                      </FormLabel>
                      <NumberInput
                        value={field.maxSelections}
                        min={1}
                        max={field.options.length || 1}
                        onChange={(valueString) =>
                          handleFieldChange(
                            fieldIndex,
                            "maxSelections",
                            parseInt(valueString)
                          )
                        }
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                      <FormErrorMessage>
                        {errors[`field_${fieldIndex}_maxSelections`]}
                      </FormErrorMessage>
                    </FormControl>
                  </Box>
                )}
                <Button
                  leftIcon={<FontAwesomeIcon icon={faTrash} />}
                  colorScheme="red"
                  mt={4}
                  onClick={() => removeField(fieldIndex)}
                >
                  ลบฟิลด์
                </Button>
              </Box>
            ))}
            <Button
              leftIcon={<FontAwesomeIcon icon={faPlus} />}
              colorScheme="iBlue"
              onClick={addField}
            >
              เพิ่มฟิลด์
            </Button>
          </VStack>
        </Box>

        <Box p={5} m={5}>
          <Checkbox
            mt={3}
            onChange={(e) => setConfirmChecked(e.target.checked)}
            size="lg"
            colorScheme="iOrange"
            isChecked={confirmChecked}
          >
            ฉันยืนยันว่าต้องการบันทึกแบบสอบถามนี้
          </Checkbox>
        </Box>

        <Box p={5} m={5}>
          <HStack spacing={4}>
            <Button
              leftIcon={<FontAwesomeIcon icon={faEye} />}
              colorScheme="gray"
              onClick={onOpen}
              isDisabled={!formData.title || formData.fields.length === 0}
            >
              ตัวอย่างแบบสอบถาม
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSubmit}
              isDisabled={!isFormValid()}
            >
              สร้างแบบสอบถาม
            </Button>
          </HStack>
        </Box>

        <Modal isOpen={isOpen} onClose={onClose} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{formData.title || "ตัวอย่างแบบสอบถาม"}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4}>
                {formData.fields.map((field, index) =>
                  renderPreviewField(field, index)
                )}
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="blue" onClick={onClose}>
                ปิด
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    </Box>
  );
};

export default QuestionnaireBuilder;
